import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiCheckCircle, 
  FiAlertCircle, 
  FiXCircle, 
  FiChevronDown, 
  FiChevronRight,
  FiFile,
  FiRefreshCw,
  FiDownload
} from 'react-icons/fi';

const ValidationPanel = ({ validationState, files, onValidation }) => {
  const [expandedFiles, setExpandedFiles] = useState(new Set());

  const toggleFileExpansion = (fileName) => {
    const newExpanded = new Set(expandedFiles);
    if (newExpanded.has(fileName)) {
      newExpanded.delete(fileName);
    } else {
      newExpanded.add(fileName);
    }
    setExpandedFiles(newExpanded);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <FiCheckCircle className="w-5 h-5 text-success-500" />;
      case 'invalid':
        return <FiXCircle className="w-5 h-5 text-error-500" />;
      case 'warning':
        return <FiAlertCircle className="w-5 h-5 text-warning-500" />;
      default:
        return <FiFile className="w-5 h-5 text-secondary-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid':
        return 'bg-success-50 border-success-200';
      case 'invalid':
        return 'bg-error-50 border-error-200';
      case 'warning':
        return 'bg-warning-50 border-warning-200';
      default:
        return 'bg-secondary-50 border-secondary-200';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'valid':
        return 'status-success';
      case 'invalid':
        return 'status-error';
      case 'warning':
        return 'status-warning';
      default:
        return 'status-info';
    }
  };

  const exportValidationReport = () => {
    const report = {
      generatedAt: new Date().toISOString(),
      summary: {
        total: validationState.results.length,
        valid: validationState.results.filter(r => r.status === 'valid').length,
        invalid: validationState.results.filter(r => r.status === 'invalid').length,
        warnings: validationState.results.filter(r => r.status === 'warning').length
      },
      results: validationState.results
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `validation-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderValidationItem = (result, index) => {
    const isExpanded = expandedFiles.has(result.fileName);
    const hasErrors = result.errors && result.errors.length > 0;
    const hasWarnings = result.warnings && result.warnings.length > 0;

    return (
      <motion.div
        key={result.fileName}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className={`border rounded-lg overflow-hidden ${getStatusColor(result.status)}`}
      >
        <div
          className="flex items-center px-4 py-3 cursor-pointer hover:bg-opacity-70"
          onClick={() => toggleFileExpansion(result.fileName)}
        >
          <div className="flex items-center flex-1">
            {getStatusIcon(result.status)}
            <div className="ml-3 flex-1">
              <div className="font-medium text-secondary-800">{result.fileName}</div>
              <div className="text-xs text-secondary-600">
                {hasErrors && `${result.errors.length} error${result.errors.length > 1 ? 's' : ''}`}
                {hasErrors && hasWarnings && ' • '}
                {hasWarnings && `${result.warnings.length} warning${result.warnings.length > 1 ? 's' : ''}`}
                {!hasErrors && !hasWarnings && 'No issues found'}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={getStatusBadgeColor(result.status)}>
              {result.status}
            </span>
            {isExpanded ? (
              <FiChevronDown className="w-4 h-4 text-secondary-500" />
            ) : (
              <FiChevronRight className="w-4 h-4 text-secondary-500" />
            )}
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (hasErrors || hasWarnings) && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t"
            >
              {hasErrors && (
                <div className="p-4 bg-error-50">
                  <h4 className="font-medium text-error-800 mb-2">Errors</h4>
                  <div className="space-y-2">
                    {result.errors.map((error, errorIndex) => (
                      <div key={errorIndex} className="text-sm text-error-700">
                        {error.line && (
                          <span className="font-mono bg-error-100 px-1 rounded">
                            Line {error.line}:
                          </span>
                        )}{' '}
                        {error.field && (
                          <span className="font-medium">{error.field}</span>
                        )}{' '}
                        {error.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {hasWarnings && (
                <div className="p-4 bg-warning-50">
                  <h4 className="font-medium text-warning-800 mb-2">Warnings</h4>
                  <div className="space-y-2">
                    {result.warnings.map((warning, warningIndex) => (
                      <div key={warningIndex} className="text-sm text-warning-700">
                        {warning.line && (
                          <span className="font-mono bg-warning-100 px-1 rounded">
                            Line {warning.line}:
                          </span>
                        )}{' '}
                        {warning.field && (
                          <span className="font-medium">{warning.field}</span>
                        )}{' '}
                        {warning.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const totalFiles = validationState.results.length;
  const validFiles = validationState.results.filter(r => r.status === 'valid').length;
  const invalidFiles = validationState.results.filter(r => r.status === 'invalid').length;
  const warningFiles = validationState.results.filter(r => r.status === 'warning').length;

  return (
    <div className="h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-secondary-800 mb-2">Validation Results</h2>
        <p className="text-secondary-600">Review data integrity and validation issues</p>
      </div>

      {/* Validation Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Total Files</p>
              <p className="text-2xl font-bold text-secondary-800">{totalFiles}</p>
            </div>
            <FiFile className="w-8 h-8 text-secondary-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Valid</p>
              <p className="text-2xl font-bold text-success-600">{validFiles}</p>
            </div>
            <FiCheckCircle className="w-8 h-8 text-success-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Warnings</p>
              <p className="text-2xl font-bold text-warning-600">{warningFiles}</p>
            </div>
            <FiAlertCircle className="w-8 h-8 text-warning-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Errors</p>
              <p className="text-2xl font-bold text-error-600">{invalidFiles}</p>
            </div>
            <FiXCircle className="w-8 h-8 text-error-500" />
          </div>
        </motion.div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between mb-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onValidation}
          disabled={validationState.isValidating}
          className={`btn-primary flex items-center space-x-2 ${
            validationState.isValidating ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <FiRefreshCw className={validationState.isValidating ? 'animate-spin' : ''} />
          <span>Run Validation</span>
        </motion.button>

        {validationState.results.length > 0 && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={exportValidationReport}
            className="btn-outline flex items-center space-x-2"
          >
            <FiDownload />
            <span>Export Report</span>
          </motion.button>
        )}
      </div>

      {/* Validation Progress */}
      {validationState.isValidating && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-white rounded-xl shadow-soft border border-secondary-100 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-secondary-700">Validating files...</span>
              <span className="text-sm text-secondary-500">{validationState.progress}%</span>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <motion.div
                className="bg-primary-600 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${validationState.progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </motion.div>
      )}

      {/* Validation Results */}
      {validationState.results.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-secondary-800">File Validation Details</h3>
          {validationState.results.map((result, index) => renderValidationItem(result, index))}
        </div>
      )}

      {/* Empty State */}
      {validationState.results.length === 0 && !validationState.isValidating && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-12 text-center"
        >
          <FiFile className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-800 mb-2">No Validation Results</h3>
          <p className="text-secondary-600 mb-6">
            Run validation to check the integrity of your data files
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onValidation}
            className="btn-primary"
          >
            Start Validation
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default ValidationPanel;